import { DateTime } from "luxon";
import { useContext } from "react";
import { useQuery } from "react-query";
import { PensionProgressSaverContext } from "../../components/pension-robot/cocktail-filler-screen/DialogueDataSaving/PensionProgressSaverContext";
import { DEFAULT_PENSION_AGE, riskProfiles } from "../../config/constants";
import { RobotEnvironmentContext } from "../../contexts/general/RobotEnvironmentContext";
import { Account, AccountType, EmploymentStatus, PensionContext, PensionParameters, RecurringPaymentInterval } from "../../contexts/pension-robot/PensionContext";
import { undefinedIfNullOrUndefinedElseValue } from "../../utils/functions";
import { buildEndpoint } from "../endpoints";
import { apifetch } from "../robotapi";
import { useGetPensionCalcData } from "./useGetPensionCalcData";

export interface PensionDialogueDataResponse {
    id?: string,
    answers?: {
        generalSituation?: {
            age?: number,
            agePartner?: number,
            retirementAge?: number,
            retirementAgePartner?: number,
            workingStatus?: string,
            workingStatusPartner?: string,
            netMonthlyIncome?: number,
            netMonthlyIncomePartner?: number,
            partnerPresent?: boolean,
            yearsAbroad?: number,
            yearsAbroadPartner?: number
        },
        monthlyNetIncomeNeeded?: number,
        monthlyNetIncomeKnownAlready?: boolean,
        pensionAow?: {
            grossYearlyAow?: number,
            grossYearlyAowPartner?: number,
            grossSecondPillarYearlyPension?: number,
            grossSecondPillarYearlyPensionPartner?: number
        },
        pensionAccounts?: ResponseAccount[],
        pensionAccountsPartner?: ResponseAccount[],
        yearSpaceParameters?: {
            pensionGrowthYearSpace?: number,
            increaseOfFor?: number,
            decreaseOfFor?: number,
            grossYearIncomeYearSpace?: number
        },
        yearSpacePensionAccount?: {
            typeOfAdditionalPensionAccount?: string,
            riskProfileId?: string,
            monthlyPaymentAmount?: number
        },
        box3InvestmentAccount?: {
            paymentSingleAmount?: number,
            paymentRecurringInterval?: string,
            paymentRecurringAmount?: number,
            riskProfileId?: string
        },
        box3SavingsAccount?: {
            paymentSingleAmount?: number,
            paymentRecurringInterval?: string,
            paymentRecurringAmount?: number
        },
        otherIncomeComponents?: {
            giftAmountOnRetirementDate?: number,
            heritageAmountOnRetirementDate?: number,
            surplusHouseAmountOnRetirementDate?: number,
            otherAmountOnRetirementDate?: number
        }
    }
};

interface ResponseAccount {
    typeOfAdditionalPensionAccount?: string | undefined;
    grossPaymentAmountOnInvestmentEndDate?: number | undefined;
    investmentEndDate?: string | undefined;
    currentValue?: number | undefined;
    paymentRecurringAmount?: number | undefined;
    paymentRecurringInterval?: string | undefined;
    riskProfileId?: string | undefined;
}

const pensionDialogueDataFromRequest = (body: PensionDialogueDataResponse | undefined, legalRetirementAge: number) => {
    const response = body?.answers;

    const getIsEmployedAbroad = (isPartner: boolean) => {
        const yearsAbroad = isPartner 
            ? response?.generalSituation?.yearsAbroadPartner
            : response?.generalSituation?.yearsAbroad;
        const aow = isPartner 
            ? response?.pensionAow?.grossYearlyAowPartner
            : response?.pensionAow?.grossYearlyAow;

        if (!!aow && aow > 0 && yearsAbroad !== null)
            return true;

        if (!!aow && aow > 0 && yearsAbroad === null)
            return false;

        return null;
    };

    const mapAccounts = (accounts: ResponseAccount[] | undefined) => {
        if (accounts === undefined) {
            const defaultAccount = [{
                type: 'investment',
                recurringPaymentInterval: 'month',
                riskProfile: riskProfiles.neutral
            }] as Account[];
            return defaultAccount;
        }

        return accounts.map(acc => {
            const typeFromResponse = !!acc.typeOfAdditionalPensionAccount ? acc.typeOfAdditionalPensionAccount as AccountType : 'investment';
            const predictedAmountAtEndAccount = acc.grossPaymentAmountOnInvestmentEndDate ?? undefined;
            const recurringPaymentAmount = !!acc.paymentRecurringAmount ? +(acc.paymentRecurringAmount) : undefined;
            return {
                type: typeFromResponse,
                currentAmount: !!acc.currentValue ? +(acc.currentValue) : undefined,
                predictedAmount: predictedAmountAtEndAccount,
                predictedAmountAtEndAccount: predictedAmountAtEndAccount,
                predictedAmountMonthly: undefined, // Set in SaveOrInvest when predicting future value of acc.
                availableAt: !!acc.investmentEndDate ? DateTime.fromISO(acc.investmentEndDate).toISODate() : undefined,
                riskProfile: typeFromResponse === "saving"
                    ? undefined
                    : acc.riskProfileId, // Assumption: API records string value that this robot puts in.
                recurringPaymentAmount: recurringPaymentAmount,
                recurringPaymentInterval: !!acc.paymentRecurringInterval ? acc.paymentRecurringInterval as RecurringPaymentInterval : 'year',
                // Would be easier if BND also added this to the endpoint so it doesn't need to be derived from
                // other values of the account
                changedManually: predictedAmountAtEndAccount && !recurringPaymentAmount,
            }
        })
    };

    const hasPartner = response?.generalSituation?.partnerPresent;

    return {
        personalInfo: {
            self: {
                age: response?.generalSituation?.age ?? undefined,
                salary: response?.generalSituation?.netMonthlyIncome ?? undefined,
                employmentStatus: response?.generalSituation?.workingStatus as EmploymentStatus ?? 'employee',
                pensionAge: response?.generalSituation?.retirementAge ?? legalRetirementAge,
            },
            maritalStatus: response?.generalSituation?.partnerPresent ? 'married' : 'single', 
            isCalculatedTogether: !!response?.generalSituation?.agePartner,
            partner: {
                age: response?.generalSituation?.agePartner ?? undefined,
                salary: response?.generalSituation?.netMonthlyIncomePartner ?? undefined,
                employmentStatus: response?.generalSituation?.workingStatusPartner as EmploymentStatus ?? 'employee',
                pensionAge: response?.generalSituation?.retirementAgePartner ?? legalRetirementAge,
            }
        },
        expensesInfo: {
            goal: response?.monthlyNetIncomeNeeded ?? undefined,
        },
        pensionInfo: {
            self: {
                isEmployedAbroad: getIsEmployedAbroad(false),
                yearsEmployedAbroad: response?.generalSituation?.yearsAbroad ?? undefined,
                aowGrossYearly: {
                    single: (!hasPartner && response?.pensionAow?.grossYearlyAow) ? response?.pensionAow.grossYearlyAow : undefined,
                    together: (hasPartner && response?.pensionAow?.grossYearlyAow) ? response?.pensionAow.grossYearlyAow : undefined
                },
                employeePensionGrossYearly: response?.pensionAow?.grossSecondPillarYearlyPension ?? undefined
            },
            partner: {
                isEmployedAbroad: getIsEmployedAbroad(true),
                yearsEmployedAbroad: response?.generalSituation?.yearsAbroadPartner ?? undefined,
                aowGrossYearly: response?.pensionAow?.grossYearlyAowPartner ?? undefined,
                employeePensionGrossYearly: response?.pensionAow?.grossSecondPillarYearlyPensionPartner ?? undefined
            }
        },
        supplementInfo: {
            self: {
                accountsAvailable: !!(response?.pensionAccounts && response.pensionAccounts.length > 0),
                accounts: mapAccounts(response?.pensionAccounts),
                additionalInvestmentAccount: {
                    isUsed: !!response?.box3InvestmentAccount?.paymentRecurringAmount || !!response?.box3InvestmentAccount?.paymentSingleAmount,
                    account: {
                        type: 'investment',
                        currentAmount: undefinedIfNullOrUndefinedElseValue(response?.box3InvestmentAccount?.paymentSingleAmount),
                        riskProfile: response?.box3InvestmentAccount?.riskProfileId ?? riskProfiles.neutral,
                        recurringPaymentAmount: undefinedIfNullOrUndefinedElseValue(response?.box3InvestmentAccount?.paymentRecurringAmount),
                        recurringPaymentInterval: response?.box3InvestmentAccount?.paymentRecurringInterval as RecurringPaymentInterval ?? 'year',
                    } as Account
                },
                additionalSavingAccount: {
                    isUsed: !!response?.box3SavingsAccount?.paymentRecurringAmount || !!response?.box3SavingsAccount?.paymentSingleAmount,
                    account: {
                        type: 'saving',
                        currentAmount: undefinedIfNullOrUndefinedElseValue(response?.box3SavingsAccount?.paymentSingleAmount),
                        riskProfile: undefined,
                        recurringPaymentAmount: undefinedIfNullOrUndefinedElseValue(response?.box3SavingsAccount?.paymentRecurringAmount),
                        recurringPaymentInterval: response?.box3SavingsAccount?.paymentRecurringInterval as RecurringPaymentInterval ?? 'year',
                    } as Account
                }
            },
            partner: {
                accountsAvailable: !!(response?.pensionAccountsPartner && response.pensionAccountsPartner.length > 0),
                accounts: mapAccounts(response?.pensionAccountsPartner)
            },
            yearSpaceParams: {
                factorA: undefinedIfNullOrUndefinedElseValue(response?.yearSpaceParameters?.pensionGrowthYearSpace),
                FORIncrease: undefinedIfNullOrUndefinedElseValue(response?.yearSpaceParameters?.increaseOfFor),
                FORDecrease: undefinedIfNullOrUndefinedElseValue(response?.yearSpaceParameters?.decreaseOfFor),
                grossYearlyIncome: undefinedIfNullOrUndefinedElseValue(response?.yearSpaceParameters?.grossYearIncomeYearSpace)
            },
            yearSpacePensionAccount: {
                typeOfAdditionalPensionAccount: !!response?.yearSpacePensionAccount?.typeOfAdditionalPensionAccount ? response.yearSpacePensionAccount.typeOfAdditionalPensionAccount as AccountType : 'saving',
                riskProfileId: response?.yearSpacePensionAccount?.riskProfileId ?? riskProfiles.neutral,
                monthlyPaymentAmount: response?.yearSpacePensionAccount?.monthlyPaymentAmount ?? undefined
            },
            otherIncomeAvailable: !!response?.otherIncomeComponents,
            otherIncome: {
                gift: response?.otherIncomeComponents?.giftAmountOnRetirementDate ?? undefined,
                heritage: response?.otherIncomeComponents?.heritageAmountOnRetirementDate ?? undefined,
                surplusHouse: response?.otherIncomeComponents?.surplusHouseAmountOnRetirementDate ?? undefined,
                other: response?.otherIncomeComponents?.otherAmountOnRetirementDate ?? undefined,
            }
        }
    } as PensionParameters;
};

export const getPensionDialogueDataUrl = (userId: string) => 
    buildEndpoint(['pensiondata', 'client'], [{key: 'id', value: userId}]);

const getPensionDialogueDataApiCall = (userId: string) =>
    apifetch<PensionDialogueDataResponse>(getPensionDialogueDataUrl(userId), 'GET');

export const useGetPensionDialogueData = () => {
    const { userToken } = useContext(RobotEnvironmentContext)
    const { actions } = useContext(PensionContext);
    const { 
        pensionProgressSaverData, 
        pensionProgressSaverData: { setRetrievedProgressSucceeded }, 
        setPensionProgressSaverData } 
    = useContext(PensionProgressSaverContext);
    const pensionCalcData = useGetPensionCalcData();
    const legalRetirementAge = pensionCalcData.data?.legalRetirementAge ?? DEFAULT_PENSION_AGE;

    const onSuccess = (data: PensionDialogueDataResponse | undefined) => {
        if ( data && !setRetrievedProgressSucceeded){
            actions.setParameters(pensionDialogueDataFromRequest(data, legalRetirementAge));
            !!setPensionProgressSaverData && setPensionProgressSaverData({ ...pensionProgressSaverData, setRetrievedProgressSucceeded: true });
        }
    };

    const query = useQuery<PensionDialogueDataResponse | undefined>(
        ['pensiondata', userToken],
        async () => { 
            if (userToken === undefined || setRetrievedProgressSucceeded)
                return undefined
            return getPensionDialogueDataApiCall(userToken)();
        },{
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            retry: false,
            enabled: false,
            onSuccess
        }
    );

    return query;
};
